import axios from 'utils/axios';

// Bounded by an explicit timeout: startCall's module-level `starting` flag
// stays true while this request is in flight, and a request that never
// settles would leave it wedged — from then on every dial is silently refused
// until a page refresh. With the timeout, axios rejects, startCall's catch
// resets `starting`, and the user gets an error card instead of a dead button.
const ACCESS_TOKEN_TIMEOUT_MS = 15000;

export async function fetchTwilioAccessToken() {
    try {
        const response = await axios.post('/api/org-id/{orgId}/clinic-id/{clinicId}/twilio/access-token', undefined, {
            timeout: ACCESS_TOKEN_TIMEOUT_MS
        });
        return response;
    } catch (e) {
        if (e && e.statusCode && e.message) {
            throw e;
        }
        throw new Error('unknown error');
    }
}

// `clinicId` is passed explicitly because the axios interceptor's {clinicId}
// substitution only fires on /clinic/... paths, and the poll outlives that.
export async function fetchGeneratedCareSessionNote(clinicId, callSid) {
    return axios.get(`/api/org-id/{orgId}/clinic-id/${clinicId}/twilio/outbound-call/${callSid}/generated-note`);
}

// Server's view of a single outbound call: { status, errorCode, errorMessage,
// toNumber, outboundCallId, answeredBy, answeredBySource, settled, needsReview,
// reviewReason, ... }. Polled after a connected call ends until the call type
// is `settled` (see the outcome poller), and polled briefly by the controller after a terminal SDK error to
// swap the browser's generic failure message for the real dial error the
// Twilio status callback persisted (the SDK only ever hears the 31000 text
// for server-rejected dials). 404 until the voice webhook inserts the row.
// `clinicId` is explicit for the same reason as the generated-note poll.
export async function fetchOutboundCallStatus(clinicId, callSid) {
    return axios.get(`/api/org-id/{orgId}/clinic-id/${clinicId}/twilio/outbound-call/${callSid}`);
}

// Mid-call consent denial: the server stops the Twilio recording and discards
// the partial recording/transcript, so no note is generated. Only valid while
// the call is in progress — the server responds 409 once the call has ended.
//
// Bounded by an explicit 15s timeout: the deny flow disables both consent
// buttons while denyRecordingConsentInFlight is true, so a request that never
// resolves would wedge them off indefinitely. On timeout axios rejects with
// code 'ECONNABORTED', which hits the controller's catch → denyRecordingConsentFailed
// (buttons re-enabled) + a "try again" snackbar, same as any other failure.
const DENY_RECORDING_CONSENT_TIMEOUT_MS = 15000;

export async function denyRecordingConsent(clinicId, callSid) {
    return axios.post(`/api/org-id/{orgId}/clinic-id/${clinicId}/twilio/outbound-call/${callSid}/deny-recording-consent`, undefined, {
        timeout: DENY_RECORDING_CONSENT_TIMEOUT_MS
    });
}

// A user's answer to what a completed call was: 'HUMAN' (spoke with someone),
// 'MACHINE' (voicemail), or null to take the answer back so the call returns
// to its automatic type. Response: { success, answeredBy, answeredBySource }
// with the type the call now shows. 409 unless the call is COMPLETED.
//
// Bounded by an explicit 15s timeout for the same reason as the consent
// denial: the call card disables its answer buttons while the save is in
// flight, so a request that never settles would wedge them off.
const SET_ANSWERED_BY_TIMEOUT_MS = 15000;

export async function setOutboundCallAnsweredBy(clinicId, outboundCallId, answeredBy) {
    return axios.put(
        `/api/org-id/{orgId}/clinic-id/${clinicId}/twilio/outbound-call/${outboundCallId}/answered-by`,
        { answeredBy },
        { timeout: SET_ANSWERED_BY_TIMEOUT_MS }
    );
}

// The patient's outbound-calling config for the call widget: the allowed "call from"
// set and the patient's default (office > clinic > org). Response: { enabled, callerId,
// defaultCallerId, callerIds: [{ callerId, display, source, officeLocationId, label }] }.
// Clinic-level feature gating belongs to api/clinicFeatures.js instead.
export async function getOutboundCallingConfig(clinicId, patientId) {
    try {
        const response = await axios.get(`/api/org-id/{orgId}/clinic-id/${clinicId}/patient/${patientId}/twilio/outbound-calling-config`);
        return response;
    } catch (e) {
        if (e && e.statusCode && e.message) {
            return e;
        }
        return {
            message: 'unknown error'
        };
    }
}
